import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DashboardService } from '@portal/app/shared/services/dashboard.service';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router
} from '@angular/router';
import { Subscription, take } from 'rxjs';
import { ViewStore } from '@portal/app/shared/state/view.store';
import { NavbarComponent } from '../navbar/navbar.component';
import { AuthService } from '@portal/app/shared/services/auth.service';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { PendoService } from '@portal/app/shared/services/pendo.service';
import { MixpanelService } from '@portal/app/shared/services/mixpanel.service';
import { SentryService } from '@portal/app/shared/services/sentry.service';
import { UserModel } from '@portal/app/shared/models/userModel';
import {
  ServerErrorService,
  ServerErrorState
} from '@portal/app/shared/services/server-error.service';
import {
  EmptyStateSize,
  EmptyStateType
} from '@design-system/components/m-empty-states';

@Component({
  selector: 'portal-main',
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.scss']
})
export class TemplateComponent implements OnInit, OnDestroy {
  public isTableauUriPath = false;
  public isMPOV2Enabled = false;
  public isOnLegacyMPODashboard = false;
  public serverErrorState: ServerErrorState = {
    hasError: false,
    isPermissionsError: false
  };

  public EmptyStateSize = EmptyStateSize;
  public EmptyStateType = EmptyStateType;
  private readonly subs: Subscription = new Subscription();

  productId = '';

  constructor(
    private readonly ref: ChangeDetectorRef,
    public router: Router,
    private readonly route: ActivatedRoute,
    private readonly dashboardService: DashboardService,
    public readonly viewStore: ViewStore,
    public readonly selectionService: SelectionService,
    public readonly pendoService: PendoService,
    public readonly mixpanelService: MixpanelService,
    public readonly sentryService: SentryService,
    public readonly authService: AuthService,
    private readonly serverErrorService: ServerErrorService
  ) {
    this.subs.add(
      this.router.events.subscribe((event: Event) => {
        switch (true) {
          case event instanceof NavigationStart: {
            // Clear server error when starting navigation to a new route
            // This allows users to navigate away from the error page
            if (this.serverErrorState.hasError) {
              this.serverErrorService.clearServerError();
            }
            break;
          }
          case event instanceof NavigationError:
          case event instanceof NavigationCancel: {
            break;
          }
          case event instanceof NavigationEnd: {
            this.isTableauUriPath = NavbarComponent.isTableauUriPath(
              location.href
            );
            this.checkIfOnLegacyMPODashboard();
            break;
          }
          default: {
            break;
          }
        }
      })
    );

    const user = this.authService.getUser() as UserModel;
    this.pendoService.setupPendo(user);
    this.mixpanelService.setupMixpanel(user);
    this.sentryService.setupSentry(user);
  }

  ngOnInit(): void {
    this.isTableauUriPath = NavbarComponent.isTableauUriPath(location.href);
    this.checkIfOnLegacyMPODashboard();

    // Subscribe to dashboard changes to update MPO V2 status
    this.subs.add(
      this.dashboardService.dashboards.subscribe(() => {
        this.checkIsMPOV2Enabled();
      })
    );

    this.subs.add(
      this.serverErrorService.errorState$.subscribe((errorState) => {
        this.serverErrorState = errorState;
        this.ref.detectChanges();
      })
    );

    this.subs.add(
      this.dashboardService.dashboards.pipe(take(1)).subscribe({
        next: (dashboards) => {
          if (dashboards.length) {
            this.ref.detectChanges();
          } else {
            this.dashboardService
              .getCurrentDashboards()
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.ref.detectChanges();
                }
              });
          }
        }
      })
    );
    this.viewStore.selectedBCPDId.subscribe((value) => {
      this.productId = value.productId;
    });
  }

  // TODO: DU: Remove this once MPO V2 is fully released
  private checkIsMPOV2Enabled(): void {
    this.isMPOV2Enabled = this.dashboardService.hasMpoV2Dashboard();
  }

  private checkIfOnLegacyMPODashboard(): void {
    // Check if current URL contains the legacy MPO dashboard identifier
    const currentUrl = this.router.url;
    this.isOnLegacyMPODashboard = currentUrl.includes('/portfolio-level');
  }

  public clearServerError(): void {
    this.serverErrorService.clearServerError();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
